import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AntAPI, AntLocalization } from 'ante-react-kit';

/**
 * Modifier editor shared by office order and web POS.
 * The persisted value stays portion-based for backward compatibility, while every option carries
 * group_code so BE can validate multiple independent groups in one reusable profile.
 */
const text = {
    unit: { vi: 'Phần', en: 'Unit', fr: 'Unité', es: 'Unidad', id: 'Unit', ja: '単位', zh: '份', ko: '단위', hi: 'इकाई', ar: 'وحدة' },
    required: { vi: 'Bắt buộc', en: 'Required', fr: 'Obligatoire', es: 'Obligatorio', id: 'Wajib', ja: '必須', zh: '必选', ko: '필수', hi: 'आवश्यक', ar: 'مطلوب' }
};
const t = (key) => AntLocalization.translateText(text[key]);

function money(value) {
    try {
        const options = AntLocalization.formatCurrency(false) || {};
        const { language, ...formatterOptions } = options;
        if (formatterOptions.style === 'currency') formatterOptions.currencyDisplay = 'narrowSymbol';
        return new Intl.NumberFormat(language || 'en-US', formatterOptions).format(Number(value) || 0);
    } catch (error) {
        return String(Number(value) || 0);
    }
}

export default function ToppingEditor({ dataField, value, formData, onValueChanged, disabled: disabledProp }) {
    const itemCode = formData ? formData.item_code : null;
    const orderQty = Math.max(1, Math.ceil(parseFloat(formData && formData.order_qty) || 1));
    const lineInKitchen =
        formData &&
        (parseFloat(formData.issue_delivery_req_qty) > 0 ||
            parseFloat(formData.issuing_qty) > 0 ||
            parseFloat(formData.issue_delivering_qty) > 0 ||
            parseFloat(formData.issue_deliveried_qty) > 0);
    const disabled = disabledProp || lineInKitchen || (formData && formData.order_status === 'Close');

    const [groups, setGroups] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoaded(false);
        setGroups([]);
        if (!itemCode) return undefined;

        (async () => {
            try {
                const itemRes = await new AntAPI('item', 'Item').getList({ code: itemCode });
                const itemRows = itemRes && itemRes.rows instanceof Array ? itemRes.rows : [];
                const assignment = itemRows[0] || {};
                const profileCode = assignment.modifier_profile_code;
                const legacyGroupCode = assignment.modifier_group_code;
                if (!profileCode && !legacyGroupCode) {
                    if (!cancelled) setLoaded(true);
                    return;
                }

                if (profileCode) {
                    const profileRes = await new AntAPI('item', 'ItemModifierProfile').getList({ code: profileCode, is_active: 1 });
                    if (!((profileRes && profileRes.rows) || []).length) {
                        if (!cancelled) setLoaded(true);
                        return;
                    }
                }

                const groupRes = await new AntAPI('item', 'ItemModifierGroup').getList(
                    profileCode ? { profile_code: profileCode, is_active: 1 } : { code: legacyGroupCode, is_active: 1 }
                );
                const groupRows = ((groupRes && groupRes.rows) || [])
                    .slice()
                    .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0));
                const groupCodes = groupRows.map((group) => group.code).filter(Boolean);
                const modifierRes = groupCodes.length
                    ? await new AntAPI('item', 'ItemModifierItem').getList({ group_code: groupCodes, is_active: 1 })
                    : { rows: [] };
                const modifierRows = (modifierRes && modifierRes.rows) || [];
                const nextGroups = groupRows
                    .map((group) => ({
                        ...group,
                        apply_scope: group.apply_scope === 'PER_LINE' ? 'PER_LINE' : 'PER_UNIT',
                        rows: modifierRows
                            .filter((row) => row.group_code === group.code)
                            .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0))
                    }))
                    .filter((group) => group.rows.length > 0);
                if (!cancelled) {
                    setGroups(nextGroups);
                    setLoaded(true);
                }
            } catch (error) {
                if (!cancelled) setLoaded(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [itemCode]);

    const portions = useMemo(() => {
        let parsedValue = value;
        if (typeof parsedValue === 'string') {
            try {
                parsedValue = JSON.parse(parsedValue);
            } catch (error) {
                parsedValue = null;
            }
        }
        // Máy chủ có thể trả một phần ăn trần (object có .options) thay vì mảng bọc ngoài — vẫn
        // đọc được thay vì coi là rỗng.
        const raw = parsedValue instanceof Array ? parsedValue : parsedValue && parsedValue.options instanceof Array ? [parsedValue] : [];
        const out = [];
        for (let index = 0; index < orderQty; index++) {
            const options = raw[index] && raw[index].options instanceof Array ? raw[index].options : [];
            out.push(options);
        }
        return out;
    }, [value, orderQty]);

    // Dòng đã chốt phải giữ nguyên topping đã chọn, kể cả khi nhóm topping sau đó bị tắt/xoá khỏi
    // cấu hình. Lúc đó không dựng lại được ô tương tác, nhưng vẫn hiện tên topping đọc từ dữ liệu
    // của chính dòng — nếu trả rỗng thì đơn cũ trông như chưa từng chọn topping, sai khi đối soát.
    const snapshot = useMemo(() => {
        const names = portions
            .map((options, index) => {
                const optNames = options
                    .filter((option) => option && option.name)
                    .map((option) => (Number(option.qty) > 1 ? option.name + ' x' + option.qty : option.name));
                if (!optNames.length) return null;
                return orderQty > 1 ? t('unit') + ' ' + (index + 1) + ': ' + optNames.join(', ') : optNames.join(', ');
            })
            .filter(Boolean);
        return names.join(' | ');
    }, [portions, orderQty]);

    const patch = useCallback(
        (portionIndex, nextOptions) => {
            const next = portions.map((options, index) => ({ options: index === portionIndex ? nextOptions : options }));
            onValueChanged({ [dataField]: next });
        },
        [portions, onValueChanged, dataField]
    );

    const optionsInGroup = useCallback(
        (portionIndex, group) => {
            const codes = new Set(group.rows.map((row) => String(row.code)));
            return (portions[portionIndex] || []).filter(
                (option) => option && (String(option.group_code || '') === String(group.code) || codes.has(String(option.code)))
            );
        },
        [portions]
    );

    const replaceGroupOptions = useCallback(
        (portionIndex, group, nextGroupOptions) => {
            const codes = new Set(group.rows.map((row) => String(row.code)));
            const otherOptions = (portions[portionIndex] || []).filter(
                (option) => option && String(option.group_code || '') !== String(group.code) && !codes.has(String(option.code))
            );
            patch(portionIndex, otherOptions.concat(nextGroupOptions));
        },
        [portions, patch]
    );

    const toggle = useCallback(
        (portionIndex, group, modifier) => {
            if (disabled) return;
            const current = optionsInGroup(portionIndex, group);
            const existed = current.find((option) => option.code === modifier.code);
            const option = {
                code: modifier.code,
                group_code: group.code,
                name: modifier.name,
                qty: 1,
                unit_price: parseFloat(modifier.unit_price) || 0
            };
            if (Number(group.max_qty) === 1) {
                replaceGroupOptions(portionIndex, group, existed ? [] : [option]);
                return;
            }
            if (existed) {
                replaceGroupOptions(
                    portionIndex,
                    group,
                    current.filter((row) => row.code !== modifier.code)
                );
                return;
            }
            const maxQty = Number(group.max_qty) > 0 ? Number(group.max_qty) : 0;
            const selectedQty = current.reduce((sum, row) => sum + (Number(row.qty) || 0), 0);
            if (maxQty > 0 && selectedQty + 1 > maxQty) return;
            replaceGroupOptions(portionIndex, group, current.concat([option]));
        },
        [disabled, optionsInGroup, replaceGroupOptions]
    );

    const step = useCallback(
        (portionIndex, group, code, delta) => {
            if (disabled) return;
            const current = optionsInGroup(portionIndex, group);
            const selected = current.find((option) => option.code === code);
            if (!selected) return;
            const maxQty = Number(group.max_qty) > 0 ? Number(group.max_qty) : 0;
            const selectedQty = current.reduce((sum, row) => sum + (Number(row.qty) || 0), 0);
            if (delta > 0 && maxQty > 0 && selectedQty + delta > maxQty) return;
            const nextQty = (Number(selected.qty) || 0) + delta;
            const next =
                nextQty <= 0
                    ? current.filter((option) => option.code !== code)
                    : current.map((option) => (option.code === code ? { ...option, qty: nextQty } : option));
            replaceGroupOptions(portionIndex, group, next);
        },
        [disabled, optionsInGroup, replaceGroupOptions]
    );

    if (!loaded) return null;
    if (groups.length === 0)
        return snapshot ? (
            <div className="ant-topping-editor">
                <div className="ate-snapshot">{snapshot}</div>
            </div>
        ) : null;

    return (
        <div className="ant-topping-editor">
            {portions.map((portionOptions, portionIndex) => {
                const visibleGroups = groups.filter((group) => group.apply_scope !== 'PER_LINE' || portionIndex === 0);
                if (visibleGroups.length === 0) return null;
                return (
                    <div key={portionIndex} className="ate-portion">
                        {orderQty > 1 && <div className="ate-portion-label">{t('unit')} {portionIndex + 1}</div>}
                        {visibleGroups.map((group) => {
                            const selected = optionsInGroup(portionIndex, group);
                            const selectedQty = selected.reduce((sum, option) => sum + (Number(option.qty) || 0), 0);
                            const requiredQty = group.is_required ? Math.max(1, Number(group.min_qty) || 0) : 0;
                            const warning = requiredQty > 0 && selectedQty < requiredQty;
                            const singleChoice = Number(group.max_qty) === 1;
                            return (
                                <section key={group.code} className="ate-group">
                                    <div className={'ate-group-label' + (warning ? ' ate-warn' : '')}>
                                        <span>{group.name}</span>
                                        {group.is_required ? <small>{t('required')}</small> : null}
                                    </div>
                                    <div className="ate-options">
                                        {group.rows.map((modifier) => {
                                            const current = selected.find((option) => option.code === modifier.code);
                                            return (
                                                <div
                                                    key={modifier.code}
                                                    className={'ate-opt' + (singleChoice ? ' ate-opt--radio' : '') + (current ? ' ate-opt--on' : '') + (disabled ? ' ate-opt--disabled' : '')}
                                                    onClick={() => (current && !singleChoice ? null : toggle(portionIndex, group, modifier))}
                                                >
                                                    <span className="ate-opt-name" onClick={(event) => { event.stopPropagation(); toggle(portionIndex, group, modifier); }}>{modifier.name}</span>
                                                    {Number(modifier.unit_price) > 0 ? <span className="ate-opt-price">+{money(modifier.unit_price)}</span> : null}
                                                    {current && !singleChoice ? (
                                                        <span className="ate-opt-stepper" onClick={(event) => event.stopPropagation()}>
                                                            <button type="button" className="ate-btn" onClick={() => step(portionIndex, group, modifier.code, -1)}>−</button>
                                                            <span className="ate-qty">{current.qty}</span>
                                                            <button type="button" className="ate-btn" onClick={() => step(portionIndex, group, modifier.code, 1)}>+</button>
                                                        </span>
                                                    ) : null}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </section>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
}
